import CheckOutlined from "@mui/icons-material/CheckOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import { Box, type SxProps, type Theme } from "@mui/material";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCopyToClipboard } from "../../../hooks/useCopyToClipboard";
import { CONTROL_HEIGHT, ICON_SIZE, INK, MOTION } from "../../../theme/tokens";
import SystemButton from "../SystemButton/SystemButton";

/** How long the "copied" check stays before the button reverts to the copy glyph. */
const COPIED_FEEDBACK_MS = 1500;

/**
 * Square size of the button box. `dense` sits inside a chip / table cell / next to small text without
 * inflating the row; `standard` is the app's 32px compact control.
 */
const SIZE_PX: Record<"dense" | "standard", string> = {
	dense: "24px",
	standard: CONTROL_HEIGHT.COMPACT,
};

type Props = {
	/** The text placed on the clipboard. */
	value: string;
	/**
	 * Hover + accessible name for the idle button. Say *what* is copied ("Copy employee name") when the
	 * surrounding context doesn't already; the bare app-wide "Copy" is used when omitted.
	 */
	label?: string;
	size?: "dense" | "standard";
	/** Glyph size — match the ink it sits beside. Defaults to the standard control glyph. */
	iconFontSize?: string;
	/** Notified after the value actually reached the clipboard — for analytics. */
	onCopied?: () => void;
	disabled?: boolean;
	sx?: SxProps<Theme>;
	dataTestId?: string;
};

/**
 * Copies one value to the clipboard and confirms it in place: the copy glyph flips to a green check
 * for a moment, so the user gets the acknowledgement at the point of the click instead of a toast
 * crossing the screen. A failed copy (a browser that denies clipboard access) is surfaced as an error
 * snackbar rather than swallowed — a silent no-op reads as "the click didn't register".
 *
 * Generic on purpose: anywhere the app shows a value someone will retype elsewhere — an ID chip, a
 * name in a page header, a file path in a table cell — is a place for this, so nobody hand-rolls the
 * icon + timer + feedback trio again.
 */
export const CopyButton: FC<Props> = ({ value, label, size = "standard", iconFontSize = ICON_SIZE.CONTROL, onCopied, disabled, sx, dataTestId }) => {
	const { t } = useTranslation();
	const { copyToClipboard } = useCopyToClipboard();
	const { showSnackbar } = useSnackbar();
	const [copied, setCopied] = useState(false);
	const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (resetTimer.current) {
				clearTimeout(resetTimer.current);
			}
		},
		[],
	);

	const handleCopy = useCallback(async () => {
		const succeeded = await copyToClipboard(value);
		if (!succeeded) {
			showSnackbar(t("system.action.copy.failed"), "error");
			return;
		}
		setCopied(true);
		onCopied?.();
		if (resetTimer.current) {
			clearTimeout(resetTimer.current);
		}
		resetTimer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
	}, [copyToClipboard, onCopied, showSnackbar, t, value]);

	const idleLabel = label ?? t("system.action.copy");
	const boxSize = SIZE_PX[size];

	return (
		<SystemButton
			buttonVariant="borderless"
			iconButton
			disabled={disabled}
			onClick={handleCopy}
			// The title swaps rather than the tooltip appearing/disappearing, so the acknowledgement lands
			// under the cursor that just clicked instead of remounting the trigger mid-click.
			tooltip={copied ? t("system.action.copied") : idleLabel}
			aria-label={idleLabel}
			data-testid={dataTestId}
			sx={[
				{
					minWidth: boxSize,
					width: boxSize,
					height: boxSize,
					// Quiet ink at rest — the button is an assist beside the value, not a call to action —
					// and the confirmation is the one moment it is allowed to be loud.
					color: copied ? "success.main" : INK.MUTED,
					transition: `color ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
					"@media (prefers-reduced-motion: reduce)": { transition: "none" },
				},
				...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box
				component="span"
				sx={{
					display: "inline-flex",
					// The check settles in from a hair under full size — a confirmation the eye catches
					// without it reading as the button itself moving.
					animation: copied ? `copy-confirm ${MOTION.DURATION.POP}ms ${MOTION.EASE_OUT}` : "none",
					"@keyframes copy-confirm": {
						from: { transform: "scale(0.9)", opacity: 0.6 },
						to: { transform: "scale(1)", opacity: 1 },
					},
					"@media (prefers-reduced-motion: reduce)": { animation: "none" },
				}}
			>
				{copied ? <CheckOutlined sx={{ fontSize: iconFontSize }} /> : <ContentCopyOutlined sx={{ fontSize: iconFontSize }} />}
			</Box>
		</SystemButton>
	);
};

export default CopyButton;
